// Pure-Functions für Trade-Log — kein Storage, kein React.
// 1:1 portiert aus V1 src/hooks/useTradingLog.js — Verhalten exakt erhalten.
// Storage-Layer kommt in stores/trading-store.ts dazu.

import type {
  CloseTradeInput,
  CreateTradeInput,
  Trade,
  TradeOutcome,
  TradingStats,
} from '@/types/trade';

/**
 * V1-Konstante: Maximale Anzahl Trades im Log. Ältere werden bei jedem `createTrade`
 * abgeschnitten. Bewusst klein, da der Log für Post-Mortem-Analyse + EVE-Kontext
 * gedacht ist, nicht als unbegrenzte History.
 */
export const MAX_TRADES = 200;

/**
 * Erzeugt eine neue Trade-ID. Bevorzugt `crypto.randomUUID()` (Node 22 + moderne
 * Browser haben das). Fallback erhält V1-Verhalten für Test-Umgebungen ohne crypto.
 */
function newTradeId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Klassifiziert das Outcome anhand des P&L. Logik 1:1 aus V1:
 * - Wenn capitalUsed gesetzt war → entscheidet pnlAbsolute (> 0 WIN, < -0.01 LOSS, sonst BREAKEVEN)
 * - Wenn capitalUsed null war → entscheidet pnlPercent (> 0.1 WIN, < -0.1 LOSS, sonst BREAKEVEN)
 *
 * Bewusst asymmetrisch: `> 0` für WIN, `< -0.01` für LOSS — kleine negative Werte
 * zählen als BREAKEVEN (Trading-Gebühren-Toleranz).
 */
export function classifyOutcome(pnlPercent: number, pnlAbsolute: number | null): TradeOutcome {
  if (pnlAbsolute !== null) {
    if (pnlAbsolute > 0) return 'WIN';
    if (pnlAbsolute < -0.01) return 'LOSS';
    return 'BREAKEVEN';
  }
  if (pnlPercent > 0.1) return 'WIN';
  if (pnlPercent < -0.1) return 'LOSS';
  return 'BREAKEVEN';
}

/**
 * Berechnet pnlPercent + pnlAbsolute für einen Close. V1-Rundungsverhalten:
 * - pnlPercent: 2 Nachkommastellen (via toFixed → parseFloat)
 * - pnlAbsolute: 2 Nachkommastellen, null wenn capitalUsed null
 */
export function computePnL(
  direction: Trade['direction'],
  entry: number,
  closePrice: number,
  capitalUsed: number | null,
): { pnlPercent: number; pnlAbsolute: number | null } {
  const rawPct =
    direction === 'LONG'
      ? ((closePrice - entry) / entry) * 100
      : ((entry - closePrice) / entry) * 100;
  const pnlPercent = parseFloat(rawPct.toFixed(2));
  const pnlAbsolute =
    capitalUsed !== null ? parseFloat(((capitalUsed * rawPct) / 100).toFixed(2)) : null;
  return { pnlPercent, pnlAbsolute };
}

/**
 * Erzeugt ein neues Trade-Record. Pure — kein Side-Effect.
 * Defaults entsprechen V1:
 * - source: 'WHALE_COPY'
 * - asset: 'BTC'
 * - capitalUsed: null wenn nicht angegeben oder 0/NaN (V1 nutzt `|| null`)
 * - status: 'OPEN'
 */
export function createTradeRecord(input: CreateTradeInput): Trade {
  return {
    id: newTradeId(),
    createdAt: new Date().toISOString(),
    source: input.source ?? 'WHALE_COPY',
    asset: input.asset ?? 'BTC',
    direction: input.direction,
    entry: input.entry,
    // V1: `parseFloat(capitalUsed) || null` — 0 und NaN werden zu null
    capitalUsed: input.capitalUsed != null && input.capitalUsed !== 0 ? input.capitalUsed : null,
    status: 'OPEN',
    closedAt: null,
    closePrice: null,
    pnlPercent: null,
    pnlAbsolute: null,
    outcome: null,
    eveCall: input.eveCall ?? '',
    btcPriceAtEntry: input.btcPriceAtEntry ?? null,
    fngAtEntry: input.fngAtEntry ?? null,
    leverage: input.leverage ?? null,
    leverageType: input.leverageType ?? null,
    liquidationPx: input.liquidationPx ?? null,
    whaleEntry: input.whaleEntry ?? null,
    notes: '',
  };
}

/**
 * Schließt einen Trade. Pure — gibt das aktualisierte Trade-Objekt zurück oder
 * `null` wenn der Trade nicht offen war.
 *
 * V1-Verhalten:
 * - Nur wenn `status === 'OPEN'` wird geschlossen
 * - status-Default: 'WHALE_EXIT' (Wallet-Monitor-Standard)
 * - notes bleibt erhalten wenn nicht überschrieben
 */
export function closeTradeRecord(trade: Trade, input: CloseTradeInput): Trade | null {
  if (trade.status !== 'OPEN') return null;
  const { pnlPercent, pnlAbsolute } = computePnL(
    trade.direction,
    trade.entry,
    input.closePrice,
    trade.capitalUsed,
  );
  return {
    ...trade,
    status: input.status ?? 'WHALE_EXIT',
    closedAt: new Date().toISOString(),
    closePrice: input.closePrice,
    pnlPercent,
    pnlAbsolute,
    outcome: classifyOutcome(pnlPercent, pnlAbsolute),
    notes: input.notes ?? trade.notes,
  };
}

/**
 * Berechnet Trading-Stats über alle geschlossenen Trades. `null` wenn keine
 * geschlossenen Trades vorliegen — V1-Verhalten.
 *
 * Strings für winRate/avgPnl/totalPnlAbs ist V1-API (toFixed-Output).
 * Konsumenten bekommen so vorgerundete Strings — bewusste Migration aus V1.
 */
export function computeStats(trades: Trade[]): TradingStats | null {
  const closed = trades.filter((t) => t.status !== 'OPEN');
  if (closed.length === 0) return null;
  const wins = closed.filter((t) => t.outcome === 'WIN').length;
  const losses = closed.filter((t) => t.outcome === 'LOSS').length;
  const winRate = ((wins / closed.length) * 100).toFixed(1);
  const totalPnlAbs = closed.reduce((s, t) => s + (t.pnlAbsolute ?? 0), 0);
  const avgPnl = closed.reduce((s, t) => s + (t.pnlPercent ?? 0), 0) / closed.length;
  return {
    total: closed.length,
    wins,
    losses,
    winRate,
    avgPnl: avgPnl.toFixed(2),
    totalPnlAbs: totalPnlAbs.toFixed(2),
  };
}

/**
 * Prüft ob ein Trade offen ist.
 */
export function isOpen(trade: Trade): boolean {
  return trade.status === 'OPEN';
}

/**
 * Filtert alle offenen Trades.
 */
export function filterOpen(trades: Trade[]): Trade[] {
  return trades.filter(isOpen);
}

/**
 * Filtert alle geschlossenen Trades (status !== 'OPEN').
 */
export function filterClosed(trades: Trade[]): Trade[] {
  return trades.filter((t) => t.status !== 'OPEN');
}

/**
 * Formatiert Trades als Text-Block für EVEs System-Prompt. V1-Format 1:1 erhalten:
 * - "OFFENE COPY-TRADES:" Sektion mit Direction/Asset/Entry/Einsatz/Quelle
 * - "LETZTE ABGESCHLOSSENE TRADES:" Sektion mit Outcome/Direction/Asset/Entry→Close/P&L/Status
 * - Maximal 10 geschlossene Trades (V1-Limit)
 * - `null` wenn keine Trades vorhanden
 */
export function formatTradesForPrompt(trades: Trade[]): string | null {
  const open = filterOpen(trades);
  const closed = filterClosed(trades).slice(0, 10);
  const lines: string[] = [];

  if (open.length > 0) {
    lines.push('OFFENE COPY-TRADES:');
    for (const t of open) {
      lines.push(
        `  ${t.direction} ${t.asset} | Entry: $${t.entry} | Einsatz: $${t.capitalUsed ?? '—'} | Quelle: ${t.eveCall}`,
      );
    }
  }

  if (closed.length > 0) {
    lines.push('\nLETZTE ABGESCHLOSSENE TRADES:');
    for (const t of closed) {
      const sign = t.pnlPercent !== null && t.pnlPercent > 0 ? '+' : '';
      lines.push(
        `  ${t.outcome} | ${t.direction} ${t.asset} | Entry $${t.entry} → Close $${t.closePrice} | P&L: ${sign}${t.pnlPercent}% | ${t.status}`,
      );
    }
  }

  return lines.length > 0 ? lines.join('\n') : null;
}
